import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: "Are these the same EAs sold on MQL5?",
    answer: "Yes, our database consists of the exact same compiled files provided by developers on MQL5, but unlocked for unrestricted use. We verify every file against the original for performance consistency."
  },
  {
    question: "Do I receive lifetime access?",
    answer: "Absolutely. Once you purchase an EA from AlgoForexEA, you receive lifetime access to the files without any recurring subscription fees or hidden costs."
  },
  {
    question: "How fast is delivery?",
    answer: "Delivery is instant for EAs listed in our Marketplace. They will be available immediately in your account dashboard. For Custom EA requests, turnaround times typically range from 24 to 48 hours."
  },
  {
    question: "Can I request any EA?",
    answer: "Yes! If an EA is not currently listed in our public marketplace, you can use our 'Request EA' service. We will scan our extensive private archives and source the EA for you at a discounted rate."
  },
  {
    question: "Will these work on prop firms?",
    answer: "Most of our EAs are fully compatible with leading prop firms (like FTMO, MyForexFunds, etc.). However, we always recommend checking your specific prop firm's rules regarding EA usage and automated trading."
  },
  {
    question: "Do you provide support?",
    answer: "Yes, we provide dedicated support. Our team is available via WhatsApp to assist with installation, broker setup, and any troubleshooting you might need to get your EA running smoothly."
  }
];

const HowItWorks = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-transparent pb-24">
      <div className="max-w-[1200px] mx-auto px-margin-mobile md:px-margin-desktop space-y-32">
        
        {/* Breadcrumb */}
        <div className="text-[11px] font-bold text-on-surface-variant mb-[-6rem] uppercase tracking-widest relative z-10">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="mx-2 opacity-50">/</span>
          <span className="text-silver-text">How It Works</span>
        </div>

        {/* 1. Hero Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#1A1B20] rounded-[2rem] border border-white/5 py-24 px-8 text-center relative overflow-hidden"
        >
          {/* Faint grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>
          
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="inline-block bg-[#2A2408] text-primary px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] mb-8 border border-primary/20">
              ALGOFOREXEA PROCESS
            </div>
            
            <h1 className="font-display-md text-headline-lg font-bold text-silver-text mb-6 tracking-tight">
              How AlgoForexEA Works
            </h1>
            
            <p className="text-on-surface-variant text-[16px] leading-relaxed mb-10 max-w-xl mx-auto">
              Get premium MT4/MT5 Expert Advisors in minutes — with verified backtests and instant delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/marketplace" className="w-full sm:w-auto bg-primary text-[#1A180B] font-bold py-4 px-8 rounded-xl hover:bg-primary/90 hover:scale-[1.02] transition-all text-[15px] btn-shimmer whitespace-nowrap">
                Browse Marketplace
              </Link>
              <Link to="/request" className="w-full sm:w-auto bg-[#23252A] text-white font-bold py-4 px-8 rounded-xl hover:bg-white/10 hover:scale-[1.02] transition-all text-[15px] border border-white/10 whitespace-nowrap">
                Request Any EA
              </Link>
            </div>
          </div>
        </motion.section>

        {/* 2. Horizontal Timeline Section */}
        <section className="relative">
          <div className="text-center mb-16">
            <h2 className="font-display-md text-[28px] font-bold text-silver-text mb-4">Your Path to Algorithmic Trading</h2>
            <p className="text-on-surface-variant text-[15px]">A simple, secure, and instant journey from browsing to automated profits.</p>
          </div>

          <div className="relative">
            {/* Horizontal Line connecting steps (Desktop only) */}
            <div className="hidden lg:block absolute top-[28px] left-[10%] right-[10%] h-px bg-white/10 z-0"></div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-6 relative z-10">
              
              {/* Step 1 */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <div className="w-14 h-14 rounded-2xl bg-[#1A1B20] border border-white/10 flex items-center justify-center text-on-surface-variant mb-6 relative group hover:border-primary/50 transition-colors">
                  <span className="material-symbols-outlined">search</span>
                  <div className="absolute -top-3 -right-3 bg-surface text-on-surface-variant text-[9px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-white/10 group-hover:text-primary transition-colors">01</div>
                </div>
                <h3 className="font-bold text-[16px] text-silver-text mb-4">Browse EAs</h3>
                <ul className="space-y-3 text-[13px] text-on-surface-variant/80">
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Explore our marketplace of MT4 and MT5 Expert Advisors</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Filter by category, platform, win rate, and strategy type</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">View complete backtest data and performance stats</span></li>
                </ul>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <div className="w-14 h-14 rounded-2xl bg-[#1A1B20] border border-white/10 flex items-center justify-center text-on-surface-variant mb-6 relative group hover:border-primary/50 transition-colors">
                  <span className="material-symbols-outlined">bar_chart</span>
                  <div className="absolute -top-3 -right-3 bg-surface text-on-surface-variant text-[9px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-white/10 group-hover:text-primary transition-colors">02</div>
                </div>
                <h3 className="font-bold text-[16px] text-silver-text mb-4">Compare & Analyze</h3>
                <ul className="space-y-3 text-[13px] text-on-surface-variant/80">
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Compare original MQL5 prices with our discounted pricing</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Analyze drawdown, profit factor, and monthly returns</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Read FAQs and trader reviews</span></li>
                </ul>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <div className="w-14 h-14 rounded-2xl bg-[#1A1B20] border border-white/10 flex items-center justify-center text-on-surface-variant mb-6 relative group hover:border-primary/50 transition-colors">
                  <span className="material-symbols-outlined">forum</span>
                  <div className="absolute -top-3 -right-3 bg-surface text-on-surface-variant text-[9px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-white/10 group-hover:text-primary transition-colors">03</div>
                </div>
                <h3 className="font-bold text-[16px] text-silver-text mb-4">Consult & Order</h3>
                <ul className="space-y-3 text-[13px] text-on-surface-variant/80">
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Connect with our support desk directly on WhatsApp</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Specify your preferred trading platform and broker settings</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Secure manual payment options and personalized invoice generation</span></li>
                </ul>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <div className="w-14 h-14 rounded-2xl bg-[#1A1B20] border border-white/10 flex items-center justify-center text-on-surface-variant mb-6 relative group hover:border-primary/50 transition-colors">
                  <span className="material-symbols-outlined">download</span>
                  <div className="absolute -top-3 -right-3 bg-surface text-on-surface-variant text-[9px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-white/10 group-hover:text-primary transition-colors">04</div>
                </div>
                <h3 className="font-bold text-[16px] text-silver-text mb-4">Personalized Delivery</h3>
                <ul className="space-y-3 text-[13px] text-on-surface-variant/80">
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Receive custom compiled EA files matching your credentials</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Includes tailor-made risk set files optimized for your broker</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Get step-by-step video installation support from our experts</span></li>
                </ul>
              </div>

              {/* Step 5 */}
              <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                <div className="w-14 h-14 rounded-2xl bg-[#1A1B20] border border-white/10 flex items-center justify-center text-on-surface-variant mb-6 relative group hover:border-primary/50 transition-colors">
                  <span className="material-symbols-outlined">bolt</span>
                  <div className="absolute -top-3 -right-3 bg-surface text-on-surface-variant text-[9px] font-bold w-6 h-6 rounded-full flex items-center justify-center border border-white/10 group-hover:text-primary transition-colors">05</div>
                </div>
                <h3 className="font-bold text-[16px] text-silver-text mb-4">Install & Trade</h3>
                <ul className="space-y-3 text-[13px] text-on-surface-variant/80">
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Add the EA to MT4 or MT5</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Configure recommended settings</span></li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✓</span> <span className="text-left">Start automated trading with your preferred broker</span></li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* 3. Why Traders Choose AlgoForexEA */}
        <section>
          <div className="text-center mb-16">
            <h2 className="font-display-md text-[28px] font-bold text-silver-text mb-4">Why Traders Choose AlgoForexEA</h2>
            <p className="text-on-surface-variant text-[15px] max-w-2xl mx-auto">We bridge the gap between expensive trading tools and everyday retail traders, offering unparalleled value.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Card 1 */}
            <div className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5 hover:border-white/10 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#2A2408] border border-primary/20 flex items-center justify-center text-primary mb-6">
                <span className="material-symbols-outlined">payments</span>
              </div>
              <h3 className="font-bold text-[15px] text-silver-text mb-3">Lower Prices Than MQL5</h3>
              <p className="text-[13px] text-on-surface-variant leading-relaxed">Save up to 80% on high-quality Expert Advisors by bypassing steep marketplace listing fees and high developer markups.</p>
            </div>

            {/* Card 2 */}
            <div className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5 hover:border-white/10 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#0E2822] border border-[#25D366]/20 flex items-center justify-center text-[#25D366] mb-6">
                <span className="material-symbols-outlined">verified</span>
              </div>
              <h3 className="font-bold text-[15px] text-silver-text mb-3">Verified Backtest Results</h3>
              <p className="text-[13px] text-on-surface-variant leading-relaxed">No black boxes. We verify performance with multi-year strategy test reports, balance/equity curves, and drawdown statistics.</p>
            </div>

            {/* Card 3 */}
            <div className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5 hover:border-white/10 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#0D1E3A] border border-[#3B82F6]/20 flex items-center justify-center text-[#3B82F6] mb-6">
                <span className="material-symbols-outlined">bolt</span>
              </div>
              <h3 className="font-bold text-[15px] text-silver-text mb-3">Instant Digital Delivery</h3>
              <p className="text-[13px] text-on-surface-variant leading-relaxed">Zero delays. Get immediate access to your files, preset files, and user documentation directly inside your account panel.</p>
            </div>

            {/* Card 4 */}
            <div className="bg-[#1A1B20] rounded-3xl p-8 border border-white/5 hover:border-white/10 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#2A1635] border border-[#A855F7]/20 flex items-center justify-center text-[#A855F7] mb-6">
                <span className="material-symbols-outlined">support_agent</span>
              </div>
              <h3 className="font-bold text-[15px] text-silver-text mb-3">Dedicated EA Request Service</h3>
              <p className="text-[13px] text-on-surface-variant leading-relaxed">Can't find a specific trading strategy or robot listing? Request it directly, and we will source and unlock it for you.</p>
            </div>

          </div>
        </section>

        {/* 4. Frequently Asked Questions */}
        <section className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display-md text-[28px] font-bold text-silver-text mb-4">Frequently Asked Questions</h2>
            <p className="text-on-surface-variant text-[15px]">Got questions about AlgoForexEA operations? Find quick answers below.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isActive = activeFaq === index;
              return (
                <div 
                  key={index}
                  className={`bg-[#1A1B20] border ${isActive ? 'border-primary/30' : 'border-white/5'} rounded-2xl overflow-hidden transition-colors duration-300`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left group"
                  >
                    <span className={`font-bold text-[14px] transition-colors duration-300 ${isActive ? 'text-primary' : 'text-silver-text group-hover:text-primary'}`}>
                      {faq.question}
                    </span>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center bg-white/5 transition-transform duration-300 ${isActive ? 'rotate-45 text-primary bg-primary/10' : 'text-on-surface-variant group-hover:text-primary group-hover:bg-primary/10'}`}>
                      <span className="material-symbols-outlined text-[16px]">add</span>
                    </div>
                  </button>
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-6 pb-6 pt-2 text-[14px] text-on-surface-variant leading-relaxed border-t border-white/5">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </section>

        {/* 5. Bottom CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-[#1A1B20] rounded-[2rem] border border-white/5 py-24 px-8 text-center relative overflow-hidden"
        >
          {/* Faint grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>
          
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="font-display-md text-headline-lg font-bold text-silver-text mb-4 tracking-tight">
              Ready to Start Automated Trading?
            </h2>
            
            <p className="text-on-surface-variant text-[16px] leading-relaxed mb-10 max-w-xl mx-auto">
              Browse our collection of verified MT4/MT5 Expert Advisors today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/marketplace" className="w-full sm:w-auto bg-primary text-[#1A180B] font-bold py-4 px-8 rounded-xl hover:bg-primary/90 hover:scale-[1.02] transition-all text-[15px] btn-shimmer whitespace-nowrap">
                Browse Marketplace
              </Link>
              <Link to="/request" className="w-full sm:w-auto bg-[#23252A] text-white font-bold py-4 px-8 rounded-xl hover:bg-white/10 hover:scale-[1.02] transition-all text-[15px] border border-white/10 whitespace-nowrap">
                Request an EA
              </Link>
            </div>
          </div>
        </motion.section>

      </div>
    </div>
  );
};

export default HowItWorks;
